import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Paper,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import apiService from '../services/api';
import ComparisonControls from '../components/comparison/ComparisonControls';
import MetricsPanel from '../components/comparison/MetricsPanel';
import SideBySideView from '../components/comparison/SideBySideView';

const TranscriptComparisonPage = () => {
  const { audioFileId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);

  const [audioInfo, setAudioInfo] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [selectedA, setSelectedA] = useState(null);
  const [selectedB, setSelectedB] = useState(null);

  const [options, setOptions] = useState({
    normalize_speaker_labels: false,
    normalize_timestamps: false,
    normalize_punctuation: true,
    case_sensitive: false,
    expand_contractions: false,
    reference_transcript: 'a', // Default to Transcript A as reference (ground truth)
  });

  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    loadTranscripts();
  }, [audioFileId]);

  const loadTranscripts = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getAudioTranscripts(audioFileId);
      setAudioInfo({
        audioFileId: data.audio_file_id,
        audioFilename: data.audio_filename,
      });
      setTranscripts(data.transcripts);

      // Auto-select transcripts: manual for A, newest machine for B
      const completedTranscripts = data.transcripts.filter(
        (t) => t.status === 'completed'
      );

      if (completedTranscripts.length >= 1) {
        // Find first manual transcript for A (reference)
        const manualTranscript = completedTranscripts.find(
          (t) => t.llm_provider === 'manual'
        );

        // Find newest non-manual transcript for B
        const machineTranscripts = completedTranscripts
          .filter((t) => t.llm_provider !== 'manual')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const newestMachine = machineTranscripts[0];

        if (manualTranscript) {
          setSelectedA(manualTranscript.id);
          // Select newest machine transcript for B
          if (newestMachine) {
            setSelectedB(newestMachine.id);
          } else if (completedTranscripts.length >= 2) {
            // If no machine transcript, select second manual
            const secondManual = completedTranscripts.find(
              (t) => t.id !== manualTranscript.id
            );
            if (secondManual) {
              setSelectedB(secondManual.id);
            }
          }
        } else {
          // No manual transcript, fall back to first two completed
          if (completedTranscripts.length >= 2) {
            setSelectedA(completedTranscripts[0].id);
            setSelectedB(completedTranscripts[1].id);
          }
        }
      }
    } catch (err) {
      console.error('Error loading transcripts:', err);
      setError(err.response?.data?.detail || 'Failed to load transcripts');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!selectedA || !selectedB) {
      return;
    }

    try {
      setComparing(true);
      setError(null);

      const result = await apiService.createComparison(
        selectedA,
        selectedB,
        options
      );

      setComparison(result);
    } catch (err) {
      console.error('Error creating comparison:', err);
      setError(err.response?.data?.detail || 'Failed to create comparison');
    } finally {
      setComparing(false);
    }
  };

  const getTranscriptById = (id) => {
    return transcripts.find((t) => t.id === id);
  };

  const transcriptAModel = comparison
    ? getTranscriptById(comparison.transcript_a_id)?.llm_model
    : null;

  const transcriptBModel = comparison
    ? getTranscriptById(comparison.transcript_b_id)?.llm_model
    : null;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading transcripts...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Typography variant="h4" gutterBottom>
          Compare Transcripts
        </Typography>

        {audioInfo && (
          <Typography variant="body2" color="text.secondary">
            Audio File: {audioInfo.audioFilename}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {transcripts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Transcripts Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This audio file has no transcripts to compare.
            Please transcribe this file with different models first.
          </Typography>
        </Paper>
      ) : transcripts.filter((t) => t.status === 'completed').length < 2 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Not Enough Transcripts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You need at least 2 completed transcripts to compare.
            Current completed transcripts: {transcripts.filter((t) => t.status === 'completed').length}
          </Typography>
        </Paper>
      ) : (
        <>
          <ComparisonControls
            transcripts={transcripts}
            selectedA={selectedA}
            selectedB={selectedB}
            options={options}
            onSelectA={setSelectedA}
            onSelectB={setSelectedB}
            onUpdateOptions={setOptions}
            onCompare={handleCompare}
            loading={comparing}
          />

          {comparison && (
            <>
              <Box mb={4}>
                <MetricsPanel
                  metrics={comparison.metrics}
                  speakerDifferences={comparison.speaker_differences}
                  referenceMode={comparison.reference_mode}
                />
              </Box>

              <Box>
                <SideBySideView
                  alignedSegments={comparison.aligned_segments}
                  transcriptAModel={transcriptAModel}
                  transcriptBModel={transcriptBModel}
                />
              </Box>
            </>
          )}

          {!comparison && !comparing && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography variant="body1" color="text.secondary">
                Select two transcripts and click "Compare Transcripts" to see the comparison
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default TranscriptComparisonPage;
